import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { showToast } from '@/globalFunction';
import { useToggle } from '@/hooks/useToggle';
import {
  checkAccess,
  MASTER_ADMIN_ROLES,
} from '@/lib/authorization/authorization';
import { queryClient } from '@/lib/react-query/react-query';
import { RootState } from '@/reducers/types';
import { parseAndSanitizeHtml } from '@/util/html';

// import Checkbox from '../../common/Elements/Checkbox/Checkbox';
import { ShadowRoot } from '../../common/Elements/ShadowRoot/ShadowRoot';
import {
  termsAndConditionsQueryKey,
  useGetTermsAndConditions,
} from '../api/getTermsAndConditions';
import { useUpdateTermsAndConditions } from '../api/updateTermsAndConditions';
import Editor from '../Elements/Editor';
import Layout from '../Elements/EditorLayout';
import MainLayout from '../Elements/MainLayout';
import { useConfirmSaveLegalDocument } from '../hooks';

const TermsAndConditions = ({ isView = false }) => {
  const [editorContent, setEditorContent] = useState(null);
  // const [isReset, toggleIsReset] = useToggle(false);
  const [isEditMode, toggleIsEditMode] = useToggle(false);
  const editorRef = useRef(null);

  const termsAndConditionsQuery = useGetTermsAndConditions({});
  const updateTermsAndConditionMutation = useUpdateTermsAndConditions();
  const [ConfirmSaveLegalDocumentModal, confirmSaveLegalDocument] =
    useConfirmSaveLegalDocument();

  useEffect(() => {
    if (!termsAndConditionsQuery.data) return;

    setEditorContent(termsAndConditionsQuery.data.termsAndConditions?.content);
  }, [termsAndConditionsQuery.data]);

  const handleClickSave = async () => {
    if (!(await confirmSaveLegalDocument())) return;

    const editorContent = editorRef.current.getContent({ format: 'raw' });

    try {
      await updateTermsAndConditionMutation.mutateAsync({
        content: editorContent,
      });
      queryClient.setQueryData(termsAndConditionsQueryKey, (prev: any) => {
        return {
          ...prev,
          termsAndConditions: {
            ...prev.termsAndConditions,
            content: editorContent,
          },
        };
      });

      showToast('success', 'Changes successsfully saved!');
      toggleIsEditMode();
    } catch (error) {
      showToast(
        'error',
        error.response.data?.message ??
          'Something went wrong please try again later.'
      );
    }
  };

  // const handleCheckReset = () => {
  //   toggleIsReset();
  // };

  const loggedUser = useSelector((state: RootState) => state.loggedUser.data);

  const isMasterAdmin = useMemo(
    () => checkAccess({ user: loggedUser, allowedRoles: MASTER_ADMIN_ROLES }),
    [loggedUser]
  );

  const isEditable = useMemo(
    () => isMasterAdmin && !isView,
    [isMasterAdmin, isView]
  );

  const htmlView = useMemo(
    () => (editorContent ? parseAndSanitizeHtml(editorContent) : null),
    [editorContent]
  );

  return (
    <>
      <MainLayout>
        <Layout
          title="Customer and User Terms and Conditions"
          isEditMode={isEditMode}
          toggleIsEditMode={toggleIsEditMode}
          onSave={handleClickSave}
          isEditable={isEditable}
        >
          {isEditMode && isEditable && (
            <div className="tw-flex tw-flex-col tw-border tw-border-cfo-grey-500 tw-rounded-b-[10px] tw-h-full">
              <div className="legal-information-editor-container tw-h-full">
                <Editor initialValue={editorContent} editorRef={editorRef} />
              </div>
              {/* <div className="tw-flex tw-justify-end tw-gap-[5px] tw-p-[20px] tw-border-t tw-border-cfo-grey-500">
              <Checkbox checked={isReset} onChange={handleCheckReset} />
              <span
                className="tw-font-brandonTextMedium hover:tw-cursor-pointer"
                onClick={handleCheckReset}
                onKeyUp={handleCheckReset}
                tabIndex={0}
                role="button"
              >
                Reset Customer and User Terms and Conditions?
              </span>
            </div> */}
            </div>
          )}

          {!isEditMode && htmlView && (
            <div className="terms-and-conditions-container">
              <ShadowRoot htmlView={htmlView} />
            </div>
          )}
        </Layout>
      </MainLayout>
      <ConfirmSaveLegalDocumentModal />
    </>
  );
};

export default TermsAndConditions;
